/**
 * (c) 2009-2024 Clinithink confidential.
 * Please see LICENSE at the project root for details.
 *
 * @file
 * Api for backend interaction
 */

import HTTP from "./http";

/**
 * API class that interacts with REST backend
 */
class Api extends HTTP {
  /**
   * Api to get all groups.
   * @returns {Promise<AxiosResponse<any>>}
   */
  async getCSRFToken() {
    const response = await this.httpGET("antiforgery/token");
    return response;
  }

  async callUserDetails() {
    const response = await this.httpGET("/api/User");
    return response;
  }

  async callAllUserGroups() {
    const response = await this.httpGET("/api/User/azure/Groups");
    return response;
  }

  async callGetRootOrg() {
    const response = await this.httpGET("/api/Organization/get-root");
    return response;
  }

  async callSuperUserCreateOrganization(payload) {
    const response = await this.httpPOST(
      "/api/Organization/create/partner-organization",
      payload
    );
    return response;
  }

  async callCreatePartnerOrganization(payload) {
    const response = await this.httpPOST(
      "/api/Organization/create/child-partner-organization",
      payload
    );
    return response;
  }

  async callUpdateOrganization(orgId, payload) {
    const response = await this.httpPUT(`/api/Organization/update/?id=${orgId}`, payload);
    return response;
  }

  async callSuperUserUpdateOrganization(orgId, payload) {
    const response = await this.httpPUT(
      `/api/Organization/super-user/update/?id=${orgId}`,
      payload
    );
    return response;
  }

  async callDeleteOrganization(orgId) {
    const response = await this.httpDELETE(`/api/Organization/delete/?id=${orgId}`);
    return response;
  }

  async callAllEnvByOrgId(orgId) {
    const response = await this.httpGET(`api/Organization/get/environements/${orgId}`);
    return response;
  }

  async callAllOrganizationsWithChildPartners(orgId) {
    const response = await this.httpGET(`api/Organization/get/child-partners/${orgId}`);
    return response;
  }

  async callOrganizationsChildsWithoutSubscription(orgId, resourceVersionId) {
    const response = await this.httpGET(
      `/api/Organization/get/children-without-subscription?orgId=${orgId}&resourceVersionId=${resourceVersionId}`
    );
    return response;
  }

  async callGetAllSearchOrganization(searchText) {
    const response = await this.httpGET(
      `/api/Organization/get-all-search?searchText=${searchText}`
    );
    return response;
  }

  async callAllEnvironment() {
    const response = await this.httpGET("/api/Environment/get-all");
    return response;
  }

  async callCreateEnvironment(formData) {
    const response = await this.httpPOST("/api/Environment/create", formData);
    return response;
  }

  async callUpdateEnvironment(envId, formData) {
    const response = await this.httpPUT(`/api/Environment/update/?id=${envId}`, formData);
    return response;
  }

  async callDeleteEnvironment(envId) {
    const response = await this.httpDELETE(`/api/Environment/delete/?id=${envId}`);
    return response;
  }

  async callUpdateCertificate(envId, formData) {
    const response = await this.httpPUT(
      `/api/Environment/certificate/update/?id=${envId}`,
      formData
    );
    return response;
  }

  async callGetCertificateDetails(formData) {
    const response = await this.httpPOST(
      "/api/Environment/get/certificate/details",
      formData
    );
    return response;
  }

  async callDataRecieptSasById(id) {
    const response = await this.httpGET(`/api/DataReceipt/get/sas/${id}`);
    return response;
  }

  async callDataRecieptFileTypesForDataReceipts(envId, pageNumber, pageSize) {
    const formdata = new FormData();
    formdata.append("environmentId", envId || "");
    formdata.append("pageNumber", pageNumber || "");
    formdata.append("pageSize", pageSize || "");
    const response = await this.httpPOST(
      "/api/DataReceipt/get/data-receipt/group-by/file-types",
      formdata
    );
    return response;
  }

  async callDataRecieptListByType(envId, dataTypeId, pageNumber, pageSize) {
    const formdata = new FormData();
    formdata.append("environmentId", envId || "");
    formdata.append("dataReceiptFileTypeId", dataTypeId || "");
    formdata.append("pageNumber", pageNumber || "");
    formdata.append("pageSize", pageSize || "");
    const response = await this.httpPOST(
      "/api/DataReceipt/get/filtered-list/by-file-type",
      formdata
    );
    return response;
  }

  async callDataReceiptFileTypes() {
    const response = await this.httpGET("/api/DataReceiptType/get-all");
    return response;
  }

  async callResourceFilteredList(payload) {
    const response = await this.httpPOST(
      "/api/Resource/get/resources/filtered-list",
      payload
    );
    return response;
  }

  async callGetManifestDetails(formData) {
    const response = await this.httpPOST("/api/Resource/get/manifest-details", formData);
    return response;
  }

  async callCreateResource(formData) {
    const response = await this.httpPOST("/api/Resource/create/new-resource", formData);
    return response;
  }

  async callCreateResourceVersion(formData) {
    const response = await this.httpPOST(
      "/api/Resource/create/resource-version",
      formData
    );
    return response;
  }

  async callSubscriptionTabResourceList(orgId, payload) {
    const response = await this.httpPOST(
      `/api/ResourceSubscription/get/resources-with-subscriptions/${orgId}`,
      payload
    );
    return response;
  }

  async callParentsOrgSubscriptionDates(orgId, resourceVersionId, payload) {
    const response = await this.httpPOST(
      `/api/ResourceSubscription/get/resources/filtered-list/${orgId}?resourceVersionId=${resourceVersionId}`,
      payload
    );
    return response;
  }

  async callUnSubscribeResourceVersion(data) {
    const response = await this.httpPOST(
      `api/ResourceSubscription/unsubscribe/resource-version?organizationId=${data.organizationId}&resourceVerionId=${data.resourceVersionId}`
    );
    return response;
  }

  async callDownloadResourceVersion(data) {
    const response = await this.httpPOST(
      `/api/Resource/download/resource-version?id=${data}`
    );
    return response;
  }

  async callDownloadResourceVersionForSuperUser(data) {
    const response = await this.httpPOST(
      `/api/Resource/super-user/download/resource-version?id=${data}`
    );
    return response;
  }

  async callCancelResourceUpdate(formData) {
    const response = await this.httpPOST(
      "/api/Resource/cancel/resource-upload",
      formData
    );
    return response;
  }

  async callAllResourceVersionsList(resourceId) {
    const response = await this.httpPOST(
      `/api/Resource/super-user/get/resource-versions/by-resource/filtered-list?ResourceId=${resourceId}`
    );
    return response;
  }

  async callOrgAdminResourceVersionsList(resourceId) {
    const response = await this.httpPOST(
      `/api/Resource/get/resource-versions/by-resource/filtered-list?ResourceId=${resourceId}`
    );
    return response;
  }

  async callResourceVersionDetails(resourceId) {
    const response = await this.httpPOST(
      `/api/Resource/get/resource-details/by-version?resourceversionId=${resourceId}`
    );
    return response;
  }

  async callDeprecateResourceVersion(resourceId) {
    const response = await this.httpPOST(
      `/api/Resource/deprecate/resource-version?id=${resourceId}`
    );
    return response;
  }

  async callDeprecateResource(resourceId) {
    const response = await this.httpPOST(
      `/api/Resource/deprecate/resource?id=${resourceId}`
    );
    return response;
  }

  async callOrganisationSubscribedToResourceVersion(payload) {
    const response = await this.httpPOST(
      "/api/Resource/get/organizations-subscribed/by-resource-version",
      payload
    );
    return response;
  }

  /**
   *
   * Api to get all partner orgs(clinithink direct childs)
   */
  async getPartnerOrgs() {
    const response = await this.httpGET("api/Organization/get/child-partners/1");
    return response;
  }

  /**
   * Api for multiple org subscription
   */
  async createMultiOrgSubscriptionForResource(payload) {
    const response = await this.httpPOST(
      "api/ResourceSubscription/create/resource-versions/multiplesubscriptions",
      payload
    );
    return response;
  }
}

export default Api;
